import { useLocation } from 'react-router';
import { getTypeSlug } from '@util/common.js';
import { categories } from '@data/categories.js';
import { useMetaTags } from 'react-metatags-hook';
import PageHeader from '@components/layout/page-header.jsx';
import { overlays } from '@data/overlays.jsx';
import { graphics } from '@data/graphics.js';
import { information } from '@data/information.js';
import { chatbots } from '@data/chatbots.js';
import ItemCard from '@components/shared/cards/item.card.jsx';
import { isTestMode } from '@util/const.js';
import { dashboards } from '@data/dashboards.jsx';

export default function CardPageLayout() {
  const location = useLocation();
  const { type } = getTypeSlug(location.pathname);

  const { description, name, pageName } = categories.find(
    (category) => category.type === type
  );

  const link = { title: pageName || name };

  let items;

  const sortItems = (a, b) => {
    if (a.isFeatured) return -1;
    else if (b.isFeatured) return 1;
    if (a.isNew || a.isUpdated) return -1;
    else if (b.isNew || b.isUpdated) return 1;
    else return a.name.localeCompare(b.name);
  };

  switch (type) {
    case 'overlays':
      items = Object.values(overlays)
        .filter((overlay) => overlay.show)
        .filter(
          (overlay) => overlay.isFeatured || overlay.isNew || overlay.isUpdated
        )
        .sort(sortItems);
      break;
    case 'dashboards':
      items = Object.values(dashboards)
        .filter((dashboard) => dashboard.show)
        .sort(sortItems);
      break;
    case 'graphics':
      items = Object.values(graphics)
        .filter(
          (bundle) =>
            bundle.show &&
            (bundle.isNew || bundle.isFeatured || bundle.isUpdated)
        )
        .sort((a, b) => a.order - b.order)
        .sort(sortItems);
      break;
    case 'information':
      items = Object.values(information).sort(sortItems);
      break;
    case 'chatbots':
      items = Object.values(chatbots).sort(sortItems);
      break;
    default:
      items = [];
  }

  useMetaTags(
    {
      title: `${pageName || name} | ${isTestMode ? '' : 'St. Jude '}Play Live Tools`,
      description,
      openGraph: {
        description,
      },
    },
    [description, name, pageName]
  );

  return (
    <div className="h-full w-full min-h-screen-nav overflow-x-hidden flex flex-col items-start lg:px-3">
      <div className="flex flex-col max-w-screen-2xl w-full overflow-hidden pt-5 pb-10">
        <PageHeader
          className="ml-6"
          name={name}
          subtitle="Featured, New, and Updated"
          description={`Use the list on the left to see all of our ${type}`}
          links={[link]}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 md:ml-6 md:mr-2 mr-2 ml-2">
          {items.map((item, index) => (
            <ItemCard
              isFirst={index === 0}
              isLast={index === items.length - 1}
              key={item.type}
              name={item.name}
              link={item.link}
              thumbnail={item.thumbnail}
              description={item.shortDescription ?? item.description}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
